import type { Dispatch } from "react";
import type { Aktion } from "../../state/reduzierer.ts";
import { bestellungLaden, SSE_ENTITAET as BESTELLUNG_ENTITAET } from "../../api/bestellungen.ts";
import { dokumentHochladen } from "../../api/dokumente.ts";
import type { Reichweite } from "../../lib/dokumentReichweite.ts";
import { NachladenNachUploadFehler } from "../../lib/upload.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";

/**
 * Order-Dokumente hochladen und den Serverstand in den gemeinsamen Speicher uebernehmen.
 * Der Erfolgs-`hinweis` (Abweichung von der Absicht) geht seit der Fehleranzeige (Paket 1) in den Fehlerstapel; einen
 * Toast gibt es hier nicht. Der Fehlschlag des Uploads selbst wird NICHT hier gemeldet (Task 5 "Uploads bis 100 MB"):
 * die Zusage lehnt mit dem urspruenglichen Fehler ab, und jeder Aufrufer meldet ihn an seiner Stelle.
 */
export function useDrawerDokumente(
  versende: Dispatch<Aktion>,
) {
  /**
   * `uploadDocFor`/`startDocumentUpload`/`addDocs` (`index.html:12673-13000`) als REST-Upload -
   * `DokumenteTab.tsx#onHochladen` (Task 19/5). Nach erfolgreichem Schreiben steht der neue
   * Dokumentstand ausschliesslich SERVERSEITIG fest (`documents.ts` liefert nur `{ document, version,
   * file, hinweis }`, kein vollstaendiges `orders`-Objekt) - dieselbe Nachlade-Form wie
   * `drawerAendern`, hier ueber `bestellungLaden`, damit `o.pdfs` im Store den frischen Stand traegt.
   *
   * ================= RICHTIGGESTELLT (Sammelsendung, Task 4b, Fixrunde 1, Befund 2) =============
   * Hier stand bis zu dieser Zeile ein „GEMESSENER BEFUND, gemeldet und NICHT repariert": `GET
   * /api/orders/:id` liefere `pdfs` ueberhaupt nicht. Das war eine Messung vom 2026-08-13 (vor
   * `items`) und ist seit dem 2026-08-15 UEBERHOLT: `Unterressourcen` (`backend/src/domaene/
   * orders.ts:236-266`) fuehrt `pdfs` als VIERTES Feld desselben Musters wie `tasks`/`payments`/
   * `items`, befuellt ueber `ladeBelegeJeBestellung` (`domaene/dokumente.ts`) - dortiger Kommentar
   * haelt ausdruecklich fest: „er ist mit dieser Zeile erledigt". Belegt durch
   * `backend/test/integration/orders-belege.test.ts:44` ("GET /api/orders/:id liefert die
   * hochgeladenen Belege als pdfs[]"). Der Nachladeschritt hier erreicht sein Ziel also WIRKLICH:
   * ein erfolgreicher Upload macht `o.pdfs` im Store frisch, GENAU wie der Funktionskommentar oben
   * es beschreibt - keine tote Absicht mehr. `test/komponenten/App/verdrahtungDrawerReiter.test.tsx`
   * (`SERVER_ANTWORT_VERSCHACHTELT`) bildet diese Antwortform bereits entsprechend nach.
   *
   * ================= `hinweis` WIRD gelesen (Sammelsendung, Task 4b) =================
   * `antwort.hinweis` (gesetzt, wenn ein sendungsweiter Typ ohne verknuepfte Sendung hochgeladen
   * wurde, siehe `api/dokumente.ts`) liest `drawerDokumentHochladen` NEBEN dem Nachladeschritt oben
   * und meldet ihn im Fehlerstapel, BEVOR nachgeladen wird (kein Toast: ein Hinweis auf eine Abweichung von der
   * Absicht darf nicht nach 2,2 s verschwinden). Kein Fehler (der Upload ist bereits gelungen),
   * aber der Hochladende soll erfahren, dass das Dokument anders als sonst bei diesem Typ ueblich
   * NUR an dieser Bestellung liegt.
   */
  /**
   * Laedt die Bestellung nach einem Dokument-Schreibzugriff neu und versendet "nachgeladen" - der
   * Nachladeschritt aus `drawerDokumentHochladen`, seit der Nachbesserung "Hash-Dedupe" (Runde 4,
   * 07.09.2026, Befund 2) als eigene Funktion herausgezogen: `DokumenteTab.tsx#duplikatBestaetigen`
   * braucht GENAU denselben Schritt nach einem erfolgreichen `POST .../documents/:docType/link`
   * (`dokumentVerknuepfen`), OHNE dabei erneut hochzuladen. Bis zu dieser Nachbesserung lud der
   * Verknuepfungs-Erfolgspfad nur die REIN LOKALE Checkliste dieses Reiters neu
   * (`checklisteNeuLaden`) - `o.pdfs`/der gruene Haken der ganzen Bestellung haengen aber am Store
   * (`Checkliste.tsx`/`DokumenteTab.tsx#pdfs`), der dabei nirgends aktualisiert wurde: ein Klick auf
   * "Verknuepfen" schloss den Dialog, sichtbar aenderte sich sonst nichts (das eigene SSE-Echo ist
   * unterdrueckt, es gab also auch darueber keinen Nachlade-Impuls).
   *
   * Bewusst OHNE eigenes `.catch` (unveraendert gegenueber vorher): ein Fehlschlag wird hier WEITERGEREICHT,
   * und jeder Aufrufer entscheidet selbst, welche Meldung ein gescheitertes Nachladen zeigt -
   * `DokumenteTab.tsx` faengt ihn an `duplikatBestaetigen` SELBST ab (siehe dortiger Kommentar). Der
   * Upload-Weg unten (`drawerDokumentHochladen`) kennzeichnet ihn ausserdem als `NachladenNachUploadFehler`.
   */
  function dokumentBestellungNachladen(bestellungId: string): Promise<void> {
    return bestellungLaden(bestellungId).then((neu) => {
      if (neu) versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id: bestellungId, datensatz: neu });
    });
  }

  /**
   * Gibt seit der Nachbesserung "Hash-Dedupe" (Punkt 4, 07.09.2026) die eigene Zusage zurueck
   * (`Promise<void>`, statt sie fire-and-forget laufen zu lassen): `DokumenteTab.tsx`s serialisierte
   * Multi-Upload-Schleife ("Sonstige Dokumente") wartet darauf, damit ein Upload wirklich
   * ABGESCHLOSSEN ist (nicht nur angestossen), bevor die naechste Datei ihre eigene
   * Duplikatpruefung beginnt - siehe dortiger Kopfkommentar an `dateiGewaehlt`.
   *
   * Seit Task 5 ("Uploads bis 100 MB") LEHNT diese Zusage bei einem Fehlschlag mit dem URSPRUENGLICHEN
   * Fehler AB, statt ihn in einem pauschalen "Das Dokument konnte nicht hochgeladen werden."
   * zu verschlucken: nur der Aufrufer kennt die Datei und kann ueber `lib/upload.ts#uploadFehlerText`
   * sagen, WAS schiefging (zu gross, Verbindung, Zeitlimit ...). `DokumenteTab.tsx` faengt den Fehler je
   * Datei ab (die uebrigen Dateien der Schleife laufen weiter) und zeigt ihn als stehenden Hinweis (den
   * Beleg-Upload des Zahlungsreiters, `App.tsx#drawerBelegHochladen`, der dasselbe tat, gibt es seit Ticket H
   * nicht mehr). Der Hook meldet bei Fehlern deshalb nichts selbst (keine Doppelmeldung im Fehlerstapel); den
   * Erfolgs-`hinweis` (siehe unten) meldet er weiter, im Fehlerstapel.
   *
   * Fix-Runde 1 zu Task 5: das gilt fuer den Upload SELBST. Ist er gelungen und scheitert nur das Nachladen
   * danach, ist die Datei gespeichert - die Zusage lehnt dann mit einem `NachladenNachUploadFehler` ab
   * (`cause`: der Fehler des Nachladens), und `uploadFehlerText` sagt "gespeichert, die Anzeige ist aber nicht
   * mehr aktuell" statt "konnte nicht hochgeladen werden". Sonst luede der Nutzer dieselbe Datei doppelt hoch.
   *
   * `fortschritt` (Task 5, siebter Parameter): der Rueckruf der Fortschrittsanzeige, unveraendert an
   * `dokumentHochladen` durchgereicht. Ist er gesetzt, reicht die Argumentliste bis zu ihm - die beiden
   * Plaetze davor sind dann ggf. `undefined` (bei gesetzter `rowVersion` bleibt `reichweite` `undefined`,
   * dieselbe Rangfolge wie ohne Fortschritt).
   *
   * `rowVersion` (Fix "Ueberschreiben scheitert immer mit 409", 07.09.2026, fuenfter Parameter):
   * unveraendert an `dokumentHochladen` durchgereicht - `DokumenteTab.tsx#ueberschreibenBestaetigen`
   * ermittelt sie ueber die Dokumenten-Checkliste (`rowVersionFuer`) und reicht sie durch
   * `hochladenAusfuehren`/`onHochladen` bis hierher durch. Bei einer Erstablage bleibt der Parameter
   * `undefined`, wie bisher.
   *
   * `reichweite` (Sammelsendung, Task 4b, MERGE 07.09.2026, sechster Parameter): die vom Hochladenden
   * getroffene Wahl aus `ReichweiteAuswahl.tsx` - unveraendert an `dokumentHochladen` durchgereicht,
   * NIE zusammen mit `rowVersion` (die beiden schliessen sich gegenseitig aus, siehe
   * `DokumenteTab.tsx#hochladenAusfuehren`). Bedingt erweiterte Argumentliste statt einer immer 6-elementigen (die
   * ungenutzten Enden ggf. `undefined`): mehrere bestehende Tests vergleichen `dokumentHochladen`s
   * Aufrufe per `toHaveBeenCalledWith` GENAU (Arraylaenge zaehlt mit) - ein zusaetzliches, explizit
   * uebergebenes `undefined` liesse sie faelschlich rot werden, obwohl sich am beobachtbaren
   * Verhalten nichts aendert.
   */
  return [
    dokumentBestellungNachladen,
    (
      bestellungId: string,
      typeId: string,
      datei: File,
      notiz?: string,
      rowVersion?: number | null,
      reichweite?: Reichweite,
      fortschritt?: (anteil: number) => void,
    ): Promise<void> => {
      const hinten = (
        fortschritt !== undefined
          ? [rowVersion, rowVersion !== undefined ? undefined : reichweite, fortschritt]
          : rowVersion !== undefined
            ? [rowVersion]
            : reichweite !== undefined
              ? [undefined, reichweite]
              : []
      ) as [rowVersion?: number | null, reichweite?: Reichweite, fortschritt?: (anteil: number) => void];
      return dokumentHochladen(bestellungId, typeId, datei, notiz, ...hinten).then(({ hinweis }) => {
        // Sammelsendung (Task 4b) - siehe Kopfkommentar oben ("`hinweis` WIRD gelesen"). Der Hinweis sagt, dass das
        // Dokument anders abgelegt wurde als gewuenscht: Abweichung von der Absicht, also Stapel.
        if (hinweis) meldeFehler(hinweis);
        return dokumentBestellungNachladen(bestellungId).catch((ursache: unknown) => {
          throw new NachladenNachUploadFehler(
            `Bestellung "${bestellungId}" konnte nach dem Hochladen von "${datei.name}" nicht neu geladen werden.`,
            { cause: ursache },
          );
        });
      });
    },
  ] as const;
}
